import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, HelpCircle } from 'lucide-react';

function PrivacyPolicy() {
  // Static Data for the Page
  const privacySections = [
    {
      title: "1. Information We Collect",
      desc: "We may collect your name, email, phone number, company details, IP address, and browsing information when you fill out our contact forms, subscribe to our services, or interact with our website."
    },
    {
      title: "2. How We Use Your Information",
      desc: (
        <ul className="space-y-3"> {/* Bullet points gap reduced */}
          <li>To provide, maintain, and improve our services</li>
          <li>To respond to your inquiries and customer support requests</li>
          <li>To send updates, newsletters, and marketing material</li>
          <li>To analyze website usage and improve user experience</li>
          <li>To comply with applicable laws and regulations</li>
        </ul>
      )
    },
    {
      title: "3. Data Protection",
      desc: "We implement security measures such as encryption, firewalls, and access controls to protect your data from unauthorized access, alteration, or disclosure. However, no system can be guaranteed 100% secure."
    },
    {
      title: "4. Your Rights",
      desc: "You have the right to request access, correction, or deletion of your personal data. You may also request restrictions on processing or object to the use of your data for marketing purposes."
    }
  ];

  return (
    <>
<Helmet>
    <title>
        Privacy Policy | Data Protection & Privacy Practices | Mobikode
    </title>

    <meta
        name="description"
        content="Learn how Mobikode collects, uses, protects, and manages personal information. Review our privacy practices, data protection measures, user rights, and information handling policies."
    />

    <meta
        name="keywords"
        content="Mobikode Privacy Policy, Privacy Policy Mobikode, Data Privacy Policy, Data Protection Policy, Personal Data Protection, Website Privacy Policy, Information Security, Data Security, Personal Information, User Privacy, Data Protection Practices, Privacy Rights, Information Handling Policy"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/privacy-policy"
    />

    <meta
        property="og:title"
        content="Privacy Policy | Data Protection & Privacy Practices | Mobikode"
    />

    <meta
        property="og:description"
        content="Read Mobikode's Privacy Policy to understand how we collect, use, protect, and manage personal information and respect user privacy."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/privacy-policy"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/what/img1.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Privacy Policy | Data Protection & Privacy Practices | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Understand Mobikode's approach to personal data, privacy, security, user rights, and responsible information handling."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/what/img1.png"
    />
</Helmet>
      <div className="min-h-screen bg-white text-[#0E2A62]">

        {/* --- Hero Section: Height reduced slightly for better flow --- */}
        <section className="relative w-full h-[450px] lg:h-[550px] flex items-center bg-[#000814] overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/what/img1.png"
              alt="Security Background"
              className="w-full h-full object-cover opacity-80"
            />
            <div style={{ background: "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)" }} className="absolute inset-0 "></div>
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

            <h1 className="text-white font-[600] text-4xl md:text-[55px] font-sora leading-[1.1] mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-300 text-base md:text-lg font-graphik font-light max-w-[700px]">
              Technology built on trust. We are committed to protecting your digital footprint.
            </p>
          </div>
        </section>

        {/* --- Static Content Section --- */}
        <section className="py-12 lg:py-20 bg-white"> {/* Section padding reduced */}
          <div className="max-w-[1000px] mx-auto px-[20px]">

            {/* Intro paragraph gap reduced */}
            <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] mb-12 leading-relaxed border-l-4 border-[#0E2A62] pl-6 text-justify">
              At Mobikode, we value your privacy and are committed to protecting your personal information.
              This Privacy Policy explains how we collect, use, and safeguard your data when you interact with our website.
            </p>

            {/* Gap between sections reduced from space-y-16 to space-y-10 */}
            <div className="space-y-10">
              {privacySections.map((section, index) => (
                <div key={index} className="border-b border-gray-100 pb-8 last:border-0">
                  <h2 className="text-[16px] md:text-[20px] font-sora font-[600] text-[#0E2A62] mb-4">
                    {section.title}
                  </h2>
                  <div className="text-[#49577A] text-[14px] md:text-[16px] font-graphik font-[500] leading-relaxed text-justify">
                    {section.desc}
                  </div>
                </div>
              ))}
            </div>

            {/* --- Help/Contact Card: Margin Top reduced --- */}
            <div className="mt-16 p-6 md:p-10 bg-[#0E2A62] text-white flex flex-col md:flex-row items-center gap-6 shadow-xl">
              <div className="bg-white/10 p-3 rounded-xl">
                <HelpCircle className="text-white" size={32} />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h4 className="text-[16px] lg:text-[20px] font-sora font-[600] mb-1">Privacy Questions?</h4>
                <p className="text-white/80 font-graphik text-[14px] md:text-[16px] font-[500]">Reach out to our privacy team at <strong>info@mobikode.com</strong></p>
              </div>
              <a
                href="mailto:info@mobikode.com"
                className="whitespace-nowrap bg-white text-[#0E2A62] px-8 py-3 rounded-full font-[500] hover:bg-gray-100 transition-all text-[14px] lg:text-[16px] font-graphik"
              >
                Contact Us
              </a>
            </div>

          </div>
        </section>

        {/* <style dangerouslySetInnerHTML={{
          __html: `
          ul li {
            position: relative;
            padding-left: 25px;
          }
          ul li::before {
            color: #0E2A62; 
            font-weight: bold;
            position: absolute;
            left: 0;
            font-size: 18px;
          }
        `}} /> */}
      </div>
    </>
  );
}

export default PrivacyPolicy;